/**
 * D1 — Pre-dispatch confirmation gate for voice campaigns.
 *
 * Renders a final-mile review surface before the campaign is created
 * and triggered. Shows:
 *   - Validated recipient summary (valid / invalid / deferred / DNC)
 *   - Estimated cost + dispatch window
 *   - Trunk + agent identity (caller-ID, agent name)
 *   - Per-campaign daily cap vs trunk cap
 *   - Mandatory checkboxes:
 *       * I attest these recipients have given consent to be called
 *       * I have reviewed the cost estimate and authorize spending
 *
 * Submit calls ``onConfirm()`` which the parent uses to actually POST
 * /api/campaigns + /api/campaigns/{id}/trigger. Cancel closes the
 * modal without dispatching; the underlying create form is preserved.
 *
 * Per v3 invariant 6: attestation gate runs server-side at dispatch.
 * Even if the operator skips this UI (e.g. legacy create flow), the
 * 412 still fires. This modal is a UX nicety, not a security gate.
 */
import React, { useState } from "react";
import { COLORS } from "../../../theme/colors";
import { Modal, Button, Badge } from "../../../components/ui";

export default function CampaignLaunchConfirmationModal({
  open,
  onClose,
  onConfirm,
  campaignName,
  trunk,
  agent,
  validation,
  dailyCapUsd,
  submitting,
}) {
  const [consentAttested, setConsentAttested] = useState(false);
  const [costAuthorized, setCostAuthorized] = useState(false);

  if (!open) return null;

  const counts = validation?.counts || {};
  const estimates = validation?.estimates || {};
  const validCount = counts.valid || 0;
  const totalCost = estimates.total_cost_usd || 0;

  const effectiveDailyCap = dailyCapUsd != null ? dailyCapUsd : (trunk?.daily_spend_cap_usd ?? 0);
  const exceedsCap = totalCost > effectiveDailyCap;

  const canSubmit =
    consentAttested && costAuthorized && validCount > 0 && !submitting && !exceedsCap;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Launch voice campaign: ${campaignName}`}
      width={580}
      footer={
        <>
          <Button onClick={onClose} variant="ghost" disabled={submitting}>Cancel</Button>
          <Button onClick={onConfirm} disabled={!canSubmit}>
            {submitting ? "Launching…" : `Launch (${validCount} call${validCount !== 1 ? "s" : ""})`}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Identity strip */}
        <div style={{ padding: 12, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>Dispatch identity</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
            <div>
              <div style={{ color: COLORS.textDim, fontSize: 10 }}>Trunk</div>
              <div style={{ color: COLORS.text, fontWeight: 500 }}>{trunk?.name || "—"}</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11 }}>Caller-ID: {trunk?.caller_id_e164 || "—"}</div>
            </div>
            <div>
              <div style={{ color: COLORS.textDim, fontSize: 10 }}>Agent</div>
              <div style={{ color: COLORS.text, fontWeight: 500 }}>{agent?.name || "—"}</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11 }}>{agent?.id?.slice(0, 8) || ""}…</div>
            </div>
          </div>
        </div>

        {/* Recipient summary */}
        <div style={{ padding: 12, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>Recipients</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Badge color={COLORS.green}>{validCount} valid</Badge>
            {(counts.deferred_quiet_hours || 0) > 0 && (
              <Badge color={COLORS.orange}>{counts.deferred_quiet_hours} deferred</Badge>
            )}
            {(counts.invalid || 0) > 0 && (
              <Badge color={COLORS.pink}>{counts.invalid} invalid (skipped)</Badge>
            )}
            {(counts.dnc || 0) > 0 && (
              <Badge color={COLORS.pink}>{counts.dnc} DNC (skipped)</Badge>
            )}
          </div>
          {validCount === 0 && (
            <div style={{ fontSize: 12, color: COLORS.pink, marginTop: 8 }}>
              No valid recipients to dispatch. Fix the list and re-validate.
            </div>
          )}
        </div>

        {/* Cost panel */}
        <div style={{ padding: 12, borderRadius: 8, background: COLORS.surface, border: `1px solid ${exceedsCap ? COLORS.pink : COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>Spend</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: exceedsCap ? COLORS.pink : COLORS.text }}>
              ${totalCost.toFixed(2)}
            </span>
            <span style={{ fontSize: 11, color: COLORS.textMuted }}>
              of ${effectiveDailyCap.toFixed(2)} daily cap
            </span>
          </div>
          {exceedsCap && (
            <div style={{ fontSize: 12, color: COLORS.pink, marginTop: 4 }}>
              ⚠ Estimated cost exceeds the daily cap. Lower max-retries, split into multiple campaigns, or raise the cap in the trunk config.
            </div>
          )}
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
            Estimate based on ~{estimates.avg_duration_seconds || 90}s avg duration @ ${estimates.rate_per_minute_usd}/min.
            Real spend depends on AMD outcomes, voicemail leaves, and answer rates.
          </div>
        </div>

        {/* Consent attestation */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: COLORS.text, cursor: "pointer" }}>
            <input type="checkbox" checked={consentAttested} onChange={e => setConsentAttested(e.target.checked)} />
            <span>
              I attest these recipients have provided prior express consent or fall under an established
              business relationship exemption per applicable jurisdiction (TCPA / EU eCommerce / equivalent).
            </span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: COLORS.text, cursor: "pointer" }}>
            <input type="checkbox" checked={costAuthorized} onChange={e => setCostAuthorized(e.target.checked)} />
            <span>
              I have reviewed the cost estimate and authorize spending up to the daily cap on this trunk.
            </span>
          </label>
        </div>

        <div style={{ fontSize: 10, color: COLORS.textDim }}>
          Audit log captures the attestation timestamp, user ID, recipient count, estimated spend, and trunk + agent IDs.
        </div>
      </div>
    </Modal>
  );
}
